import React, { useCallback } from 'react';
import { EmblaOptionsType } from 'embla-carousel';
import { useMediaQuery } from 'react-responsive';
import useEmblaCarousel from 'embla-carousel-react';
import { useSelector } from 'react-redux';
import { device } from '../../styles/devices';
import { StyledWrapper, ContentPadding } from './EmblaCarousel.styles';
import { useDotButton } from './EmblaCarouselDotButton';
import { usePrevNextButtons } from './EmblaCarouselArrowButtons';
import {
  MobileImage,
  DesktopImageContainer,
  DesktopImage,
  DesktopTile,
  MobileTile,
  OfferTypeWrapper,
} from '../SlideShow/SlideShow.styles';
import {
  MobileController,
  DesktopController,
  Label,
  PaginationWrapper,
} from '../SlideShow/Carousel.styles';
import ArrowButtons from '../SlideShow/ArrowButtons';
import SlideContent from '../FeaturedOffers/SlideContent';
import PaginationDots from '../SlideShow/PaginationDots';
import { PromotionType } from '../../types/offers';
import { selectZipCode } from '../../reducers/zipCodeReducer';
import { selectDealerContextId } from '../../reducers/dealerContextReducer';
import OfferTypeTags from './OfferTypeTags';

interface CarouselProps {
  slides: PromotionType[];
  options?: EmblaOptionsType;
}

const EmblaCarousel: React.FC<CarouselProps> = ({ slides, options }) => {
  const isLaptop = useMediaQuery({ query: device.laptop });
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const { selectedIndex, onDotButtonClick } = useDotButton(emblaApi);

  const { dealerId } = useSelector(selectDealerContextId);

  const { confirmedZipcode } = useSelector(selectZipCode);

  const { onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  const renderTags = useCallback(
    (offer: PromotionType) => {
      return <OfferTypeTags offer={offer} dealerId={dealerId} />;
    },
    [dealerId],
  );

  if (!slides.length) return null;

  return (
    <StyledWrapper>
      <ContentPadding>
        <section className="embla">
          <MobileController>
            <PaginationDots
              currentIndex={selectedIndex}
              totalSlides={slides.length}
              goToSlide={onDotButtonClick}
            />
            <ArrowButtons goToNext={onNextButtonClick} goToPrev={onPrevButtonClick} />
          </MobileController>
          <div className="embla__viewport" ref={emblaRef}>
            <div data-testid="slides-container" className="embla__container">
              {slides.map((item, index) => (
                // add offer id here instead of zipClusterID
                // eslint-disable-next-line react/no-array-index-key
                <div className="embla__slide" key={index}>
                  {/* <div className="embla__slide__number">{index + 1}</div> */}
                  {isLaptop ? (
                    <DesktopTile isActive={index === selectedIndex}>
                      <SlideContent
                        offer={item}
                        confirmedZipcode={confirmedZipcode}
                        isActive={index === selectedIndex} // Pass isActive prop here
                      />
                      <DesktopImageContainer>
                        <OfferTypeWrapper>{renderTags(item)}</OfferTypeWrapper>
                        <DesktopImage
                          isActive={index === selectedIndex}
                          data-testid="featured-offer-desktop-image"
                          src={
                            item.featuredOfferImageUrl ??
                            'https://nar.media.audi.com/is/image/audinar/nemo/us/offers/fallbacks/FeaturedOffer1.png'
                          }
                          // add offer id here instead of zipClusterID
                          alt={`Featured offer ${item.zipClusterID} banner image`}
                        />
                      </DesktopImageContainer>
                    </DesktopTile>
                  ) : (
                    <MobileTile isActive={index === selectedIndex}>
                      <OfferTypeWrapper>{renderTags(item)}</OfferTypeWrapper>
                      <MobileImage
                        src={
                          item.featuredOfferImageUrl ??
                          'https://nar.media.audi.com/is/image/audinar/nemo/us/offers/fallbacks/FeaturedOffer1.png'
                        }
                        // add offer id here instead of zipClusterID
                        alt={`Featured offer ${item.zipClusterID} banner image`}
                      />
                      <SlideContent
                        confirmedZipcode={confirmedZipcode}
                        offer={item}
                        isActive={index === selectedIndex} // Pass isActive prop here
                      />
                    </MobileTile>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DesktopController>
            <PaginationWrapper>
              <Label data-testid="pagination-label">Featured offers</Label>
              <PaginationDots
                currentIndex={selectedIndex}
                totalSlides={slides.length}
                goToSlide={onDotButtonClick}
              />
            </PaginationWrapper>
            <ArrowButtons goToNext={onNextButtonClick} goToPrev={onPrevButtonClick} />
          </DesktopController>
        </section>
      </ContentPadding>
    </StyledWrapper>
  );
};

export default EmblaCarousel;
